import type { ReactNode } from "react";
import { Fragment, useCallback, useEffect, useState } from "react";
import {
  Button,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import type { StoreProps } from "store/store";
import { deleteArticle } from "reducers/cartReducer";
import type CommandeArticleCartModel from "models/CommandeArticleCartModel";
import DeleteIcon from "@mui/icons-material/Delete";
import SquareImage from "components/atoms/SquareImage";
import articlesService from "services/articlesService";
import commandeArticleUtil from "utils/commandeArticleUtil";
import commandesService from "services/commandesService";
import type Article from "models/Article";
import { enqueueSnackbar } from "notistack";
import ToastMessages from "constants/ToastMessages";
import type Commande from "models/Commande";

function CartTable(): ReactNode {
  const cart = useSelector<StoreProps, Array<CommandeArticleCartModel>>((state) => state?.cart);
  const [articles, setArticles] = useState<Array<Article>>([]);
  const [commandeHistory, setCommandeHistory] = useState<Array<Commande>>([]);

  useEffect(() => {
    async function getArticles(): Promise<void> {
      try {
        const allArticles = await articlesService.getAll();
        allArticles.sort((p1, p2) => p1.label.localeCompare(p2.label));
        setArticles(allArticles);
      } catch {
        enqueueSnackbar(ToastMessages.ERROR_RETRY, { variant: "error" });
        setArticles([]);
      }
    }

    async function getCommandeHistoryForCurrentUser(): Promise<void> {
      try {
        const history = await commandesService.getAllByMe();
        setCommandeHistory(history);
      } catch {
        setCommandeHistory([]);
      }
    }

    void getArticles();
    void getCommandeHistoryForCurrentUser();
  }, []);

  const dispatch = useDispatch();
  const isMediumScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const isVerySmallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const getLimitExceeded = useCallback(
    (article: CommandeArticleCartModel): boolean => {
      const foundArticle = articles.find((a) => a.id === article.articleId);
      const commandeArticleHistory = commandeHistory.flatMap((c) => c.articles);
      return (
        foundArticle != null &&
        commandeArticleUtil.isLimitExceeded(commandeArticleHistory, foundArticle, article.quantite)
      );
    },
    [commandeHistory, articles]
  );

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <Typography>
                <strong>Article</strong>
              </Typography>
            </TableCell>
            <TableCell sx={isVerySmallScreen ? { paddingX: 0 } : {}}>
              <Typography>
                <strong>{isMediumScreen ? "Qté" : "Quantité"}</strong>
              </Typography>
            </TableCell>
            <TableCell>
              <Typography>
                <strong>Taille</strong>
              </Typography>
            </TableCell>
            <TableCell align="left" sx={isVerySmallScreen ? { paddingX: 0 } : {}}></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {cart.map((article) => (
            <Fragment key={article.articleId + article.taille + article.quantite.toString()}>
              <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                <TableCell align="left">
                  <Grid container columnSpacing={1} size={12} sx={{ alignItems: "center" }}>
                    <Grid>
                      <SquareImage
                        image={
                          articles?.find((a) => article.articleId === a.id)?.photo.sasUri ?? ""
                        }
                        width={isMediumScreen ? "70px" : "140px"}
                        withBackground
                      />
                    </Grid>
                    <Grid size="grow">
                      <Typography>{article.articleLabel}</Typography>
                    </Grid>
                  </Grid>
                </TableCell>
                <TableCell>
                  <Typography>{article.quantite}</Typography>
                </TableCell>
                <TableCell>
                  <Typography>{article.taille}</Typography>
                </TableCell>
                <TableCell sx={isVerySmallScreen ? { p: 0 } : {}}>
                  {isMediumScreen ? (
                    <IconButton color="error" onClick={() => dispatch(deleteArticle(article))}>
                      <Tooltip title="Supprimer l'article">
                        <DeleteIcon />
                      </Tooltip>
                    </IconButton>
                  ) : (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => dispatch(deleteArticle(article))}
                      endIcon={<DeleteIcon />}>
                      Supprimer
                    </Button>
                  )}
                </TableCell>
              </TableRow>
              {/* Check for limit exceeded */}
              {getLimitExceeded(article) && (
                <TableRow>
                  <TableCell colSpan={4} sx={{ paddingY: 0 }}>
                    <Typography color="error">
                      Vous vous apprêtez à dépasser la limite de commande de cet article, votre
                      demande va être étudiée
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </Fragment>
          ))}
          {cart.length === 0 && (
            <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
              <TableCell align="center" colSpan={4}>
                <Typography>Votre panier est vide</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default CartTable;
